import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';

const UserGuide: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="settings">
      <div className="settings-header">
        <button onClick={() => navigate('/settings')} className="btn-secondary" style={{ marginBottom: '1rem' }}>
          ← Back to Settings
        </button>
        <h1><Icon name="book" className="icon" />User Guide</h1>
        <p>A beta user's guide to pantry tracking, AI meal planning, and shopping lists.</p>
      </div>

      <div className="settings-grid">
        <div className="settings-card">
          <Icon name="sparkle" className="icon card-icon" />
          <h2>Quick Start</h2>
          <ol className="instructions-list">
            <li>Sign in with the username and password your household admin set up for you.</li>
            <li>In Settings, set your Lifestyle, Allergies &amp; Restrictions, and Planning preferences. New here? Load Sample Pantry data first.</li>
            <li>Click Generate 10-Day Plan and wait 30&ndash;90 seconds &mdash; you'll land on your Meal Plan automatically.</li>
            <li>Check Shopping List for what to buy. Tap In Pantry as you unpack groceries.</li>
          </ol>
        </div>

        <div className="settings-card">
          <Icon name="basket" className="icon card-icon" />
          <h2>Pantry</h2>
          <p>
            Tap <strong>+ Add Item</strong> and choose <strong>Scan Receipt</strong> (photograph a receipt to add
            several items at once), <strong>Scan Item</strong> (photograph a single item to identify it), or{' '}
            <strong>Enter Manually</strong> (type it in, or use Voice Input). Every item tracks quantity, unit,
            category, and a freshness score &mdash; 80%+ is fresh, 50&ndash;79% use it soon, below 50% is past its
            prime. Bought something on your Shopping List? Tap <strong>In Pantry</strong> there instead of
            re-entering it here.
          </p>
        </div>

        <div className="settings-card">
          <Icon name="calendar" className="icon card-icon" />
          <h2>Meal Plan</h2>
          <p>
            Your home screen: a rolling 10-day, 3-meals-a-day schedule. Each card offers{' '}
            <strong>View Recipe</strong>, <strong>Cook</strong> (marks it Cooked), a heart to save it to My
            Recipes, a move icon to reschedule or swap with another meal, and a trash icon to remove it.
          </p>
        </div>

        <div className="settings-card">
          <Icon name="flame" className="icon card-icon" />
          <h2>I'm Hungry</h2>
          <p>
            For an idea right now: pick a meal type, cuisine, and servings, and RecipeEngine prioritizes recipes
            that use what's already in your Pantry. Suggestions are tagged <strong>Ready to cook</strong> when
            your Pantry covers them, or show exactly what's missing.
          </p>
        </div>

        <div className="settings-card">
          <Icon name="book" className="icon card-icon" />
          <h2>My Recipes</h2>
          <p>
            Your personal cookbook &mdash; liked and saved recipes are considered first in future meal plans and
            I'm Hungry runs. Add your own via <strong>Import from URL</strong>, <strong>Paste Recipe Text</strong>,
            or <strong>Enter Manually</strong>. Imports open a review screen first; nothing saves until you confirm.
            Most recipes open with an <strong>At a Glance</strong> card &mdash; tap it to go full screen and turn
            your phone sideways for the best view.
          </p>
        </div>

        <div className="settings-card">
          <Icon name="cart" className="icon card-icon" />
          <h2>Shopping List</h2>
          <p>
            Built automatically from your Meal Plan, netted against what's already in your Pantry. Items are
            split into <strong>Items to Purchase</strong> and <strong>Already in Stock</strong>. Tap{' '}
            <strong>In Pantry</strong> on anything you've bought to move it into your Pantry and off the list.
          </p>
        </div>

        <div className="settings-card">
          <Icon name="gear" className="icon card-icon" />
          <h2>Settings</h2>
          <p>
            Set your <strong>Lifestyle</strong> (diet), <strong>Planning</strong> (which meals to plan for),{' '}
            <strong>Time &amp; Difficulty</strong> (skill level and how long you have), and{' '}
            <strong>Allergies &amp; Restrictions</strong> &mdash; every recipe suggestion respects these.{' '}
            <strong>Generate 10-Day Plan</strong> replaces your current plan and usually takes under 90 seconds.
            The <strong>Danger Zone</strong> can purge your pantry or delete your meal plan, but always leaves a
            one-tap Restore right there afterward.
          </p>
        </div>

        <div className="settings-card">
          <Icon name="warning" className="icon card-icon" />
          <h2>Beta Notes</h2>
          <p>
            This is v1.0.0 (Beta) &mdash; a small group of users has access, and what you run into now shapes
            what ships next. Robotic kitchen device control is planned for a later phase and isn't part of the
            app today. AI steps occasionally time out; retrying almost always works. Found a bug or something
            confusing? Pass it along to your household admin.
          </p>
        </div>
      </div>

      <button onClick={() => navigate('/settings')} className="btn-secondary" style={{ marginTop: '2rem' }}>
        ← Back to Settings
      </button>
    </div>
  );
};

export default UserGuide;
